const UNKNOWN_RECORD_TYPE = "Record<string, unknown>";
const IDENTIFIER_RE = /^[$A-Z_][0-9A-Z_$]*$/i;

function safeFieldKey(key: string): string {
  return IDENTIFIER_RE.test(key) ? key : JSON.stringify(key);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getObjectProperty(value: unknown, key: string): unknown {
  return isPlainObject(value) ? value[key] : undefined;
}

function hasParseFunction(value: unknown): boolean {
  return typeof getObjectProperty(value, "parse") === "function";
}

function inferTypeFromSample(sample: unknown): string {
  if (sample instanceof Date && !Number.isNaN(sample.getTime())) return "Date";
  if (sample === undefined || sample === null) return "unknown";
  const typeOfSample = typeof sample;
  if (typeOfSample === "string") return "string";
  if (typeOfSample === "number") return "number";
  if (typeOfSample === "boolean") return "boolean";
  if (typeOfSample === "bigint") return "bigint";
  if (Array.isArray(sample)) return "unknown[]";
  if (typeOfSample === "object") return UNKNOWN_RECORD_TYPE;
  return "unknown";
}

function jsonSchemaToTypeLiteral(jsonSchema: unknown): string | undefined {
  if (!jsonSchema || typeof jsonSchema !== "object" || Array.isArray(jsonSchema)) return undefined;
  const schema = jsonSchema as Record<string, unknown>;
  if (Array.isArray(schema.anyOf) && schema.anyOf.length > 0) {
    const branches = schema.anyOf
      .map((branch: unknown) => jsonSchemaToTypeLiteral(branch))
      .filter(Boolean) as string[];
    if (branches.length > 0) return branches.join(" | ");
  }
  const type = schema.type;
  if (Array.isArray(type) && type.length > 0) {
    const rendered = type
      .map((entryType) => jsonSchemaToTypeLiteral({ ...schema, type: entryType }))
      .filter(Boolean);
    return rendered.length > 0 ? rendered.join(" | ") : undefined;
  }
  if (typeof type !== "string") {
    if (Array.isArray(schema.enum) && schema.enum.length > 0) {
      return schema.enum.map((value: unknown) => JSON.stringify(value)).join(" | ");
    }
    return undefined;
  }
  if (type === "string" && schema.format === "date-time") return "Date";
  if (type === "string" && schema.format === "binary" && schema["x-gencow-type"] === "file") {
    return "File";
  }
  if (type === "string") return "string";
  if (type === "number" || type === "integer") return "number";
  if (type === "boolean") return "boolean";
  if (type === "null") return "null";
  if (type === "array") return `(${jsonSchemaToTypeLiteral(schema.items) ?? "unknown"})[]`;
  if (type === "object") {
    const props = schema.properties;
    const required = new Set(Array.isArray(schema.required) ? schema.required : []);
    if (!isPlainObject(props)) return UNKNOWN_RECORD_TYPE;
    const fields = Object.entries(props).map(([field, childSchema]) => {
      const optional = required.has(field) ? "" : "?";
      return `${safeFieldKey(field)}${optional}: ${jsonSchemaToTypeLiteral(childSchema) ?? "unknown"}`;
    });
    return `{ ${fields.join("; ")} }`;
  }
  return undefined;
}

function renderZodObjectShape(shape: unknown): string {
  if (!isPlainObject(shape)) return UNKNOWN_RECORD_TYPE;
  const entries = Object.entries(shape);
  if (entries.length === 0) return "Record<string, never>";
  const rendered = entries.map(([field, fieldSchema]) => {
    return `${safeFieldKey(field)}: ${schemaToCodegenTypeLiteral(fieldSchema)}`;
  });
  return `{ ${rendered.join("; ")} }`;
}

function zodSchemaToTypeLiteral(schema: unknown): string | undefined {
  const def = getObjectProperty(schema, "_def");
  if (!isPlainObject(def)) return undefined;
  const type = def.type;
  if (typeof type !== "string") return undefined;
  if (type === "string") return "string";
  if (type === "number") return "number";
  if (type === "boolean") return "boolean";
  if (type === "bigint") return "bigint";
  if (type === "date") return "Date";
  if (type === "null") return "null";
  if (type === "undefined") return "undefined";
  if (type === "unknown" || type === "any") return "unknown";
  if (type === "void") return "void";
  if (type === "never") return "never";
  if (type === "literal") return JSON.stringify(def.value);
  if (type === "array") return `(${schemaToCodegenTypeLiteral(def.element)})[]`;
  if (type === "object") return renderZodObjectShape(def.shape);
  if (type === "map") {
    return `Map<${schemaToCodegenTypeLiteral(def.keyType)}, ${schemaToCodegenTypeLiteral(def.valueType)}>`;
  }
  if (type === "record") return `Record<string, ${schemaToCodegenTypeLiteral(def.valueType)}>`;
  if (type === "tuple") {
    return Array.isArray(def.items)
      ? `[${def.items.map((item: unknown) => schemaToCodegenTypeLiteral(item)).join(", ")}]`
      : "unknown[]";
  }
  if (type === "union") {
    return Array.isArray(def.options)
      ? def.options.map((option: unknown) => schemaToCodegenTypeLiteral(option)).join(" | ")
      : "unknown";
  }
  if (type === "optional") return `${schemaToCodegenTypeLiteral(def.innerType)} | undefined`;
  if (type === "nullable") return `${schemaToCodegenTypeLiteral(def.innerType)} | null`;
  if (type === "default") return schemaToCodegenTypeLiteral(def.innerType);
  return undefined;
}

export function schemaToCodegenTypeLiteral(schema: unknown): string {
  if (!schema) return UNKNOWN_RECORD_TYPE;

  const fromDirectJsonSchema = jsonSchemaToTypeLiteral(schema);
  if (fromDirectJsonSchema) return fromDirectJsonSchema;

  const standardMeta = getObjectProperty(schema, "~standard");
  const standardJsonSchema = getObjectProperty(standardMeta, "jsonSchema");
  const fromStandardJsonSchema = jsonSchemaToTypeLiteral(
    typeof standardJsonSchema === "function" ? (standardJsonSchema as () => unknown)() : standardJsonSchema,
  );
  if (fromStandardJsonSchema) return fromStandardJsonSchema;

  const vendorProp = getObjectProperty(standardMeta, "vendor");
  const vendor = typeof vendorProp === "string" ? vendorProp.toLowerCase() : "";
  if (vendor.includes("zod")) {
    const fromZod = zodSchemaToTypeLiteral(schema);
    if (fromZod) return fromZod;
  }

  if (hasParseFunction(schema)) return inferTypeFromSample(getObjectProperty(schema, "_type"));
  if (!isPlainObject(schema)) return UNKNOWN_RECORD_TYPE;
  const entries = Object.entries(schema).filter(([, value]) => hasParseFunction(value));
  if (entries.length === 0) return UNKNOWN_RECORD_TYPE;
  const rendered = entries.map(([field, validator]) => {
    const validatorType = getObjectProperty(validator, "_type");
    const inferred = inferTypeFromSample(validatorType);
    const optional = validatorType === undefined ? "?" : "";
    return `${safeFieldKey(field)}${optional}: ${inferred}`;
  });
  return `{ ${rendered.join("; ")} }`;
}
